import { useCallback, useEffect, useState } from "react";
import api from "../../../config/axios";
import { Sector, ZonasConfig } from "../types/Sector";
import { logger } from "../../../utils/logger";
import { INTERNAL_DEPARTMENTS } from "../utils/internalDepartments";

type ReloadZonasOptions = {
  silent?: boolean;
};

const sameStringList = (left: string[], right: string[]) =>
  left.length === right.length &&
  left.every((item, index) => item === right[index]);

const sameZones = (left: Sector[], right: Sector[]) =>
  left.length === right.length &&
  left.every(
    (zone, index) =>
      zone.id === right[index].id &&
      zone.nombre === right[index].nombre &&
      sameStringList(zone.departamentos, right[index].departamentos)
  );

export const useZonas = () => {
  const [zonas, setZonas] = useState<Sector[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargarZonas = useCallback(async (options: ReloadZonasOptions = {}) => {
    const silent = Boolean(options.silent);
    try {
      if (!silent) setLoading(true);
      const response = await api.get<ZonasConfig>("/departamentos/zonas");
      const zonasData = response.data;

      const zonasFormateadas = [
        {
          id: "centro",
          nombre: zonasData.centro.nombre,
          departamentos: zonasData.centro.departamentos,
        },
        {
          id: "loma",
          nombre: zonasData.loma.nombre,
          departamentos: zonasData.loma.departamentos,
        },
        {
          id: "interno",
          nombre: zonasData.interno?.nombre || "Interno",
          departamentos:
            zonasData.interno?.departamentos ||
            INTERNAL_DEPARTMENTS.map((d) => (d === "boedo" ? "Boedo" : d)),
        },
      ];

      setZonas((current) =>
        sameZones(current, zonasFormateadas) ? current : zonasFormateadas
      );
      setError(null);
    } catch (error) {
      logger.errorWithContext("Error al cargar zonas", error);
      // En caso de error, usar una configuración por defecto que coincida exactamente con el backend
      const zonasPorDefecto = [
        {
          id: "centro",
          nombre: "Centro",
          departamentos: [
            "01",
            "02",
            "03",
            "04",
            "05",
            "06",
            "07",
            "08",
            "09",
            "10",
            "11",
            "12A",
            "12B",
            "14",
            "15",
            "16",
            "17",
            "18",
          ],
        },
        {
          id: "loma",
          nombre: "Loma",
          departamentos: [
            "19",
            "20",
            "21",
            "22",
            "23",
            "24",
            "25",
            "26",
            "27",
          ],
        },
        {
          id: "interno",
          nombre: "Interno",
          departamentos: ["Boedo"],
        },
      ];
      logger.info("Usando zonas por defecto", { zonas: zonasPorDefecto });
      setZonas((current) =>
        sameZones(current, zonasPorDefecto) ? current : zonasPorDefecto
      );
      setError("Error al cargar las zonas desde el servidor");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarZonas();
  }, [cargarZonas]);

  return { zonas, loading, error, reloadZonas: cargarZonas };
};
